<template>
  <div
    ref="rootElem"
    :class="rootClasses"
    data-track-as="collapsible"
    :data-track-text="titleLabel"
  >
    <div class="collapsible__action" @click="toggle">
      <h2 class="collapsible__title">
        {{ titleLabel }}
      </h2>
      <dotcom-icon
        class="collapsible__indicator"
        name="chevron-right"
        art="regular"
        :color="isReduced ? '--c-primary-color-3' : '--c-secondary-neutral-2'"
        :rotation="open ? 270 : 90"
        :class="[{ 'collapsible__indicator--reduced': isReduced }]"
      />
    </div>
    <div ref="collapsibleContent" class="collapsible__content">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'

export default {
  name: 'SanitasCollapsible',
  components: {
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
  },
  props: {
    accordion: Boolean,
    isOpen: Boolean,
    isReduced: Boolean,
    titleLabel: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      open: this.isOpen,
    }
  },

  computed: {
    rootClasses() {
      return {
        'accordion-item': this.accordion,
        'collapsible': !this.accordion,
        'is-open': this.open,
        'is-closed': !this.open,
        'is-reduced': this.isReduced,
      }
    },
  },
  watch: {
    isOpen: {
      immediate: true,
      handler() {
        this.open = this.isOpen
      },
    },
  },
  created() {
    window.addEventListener('resize', debounce(this.getAndSetHeight, 300))
  },
  mounted() {
    if (this.$refs.collapsibleContent.children[0]) {
      this.$refs.collapsibleContent.children[0].style.display = 'block'
    }
    this.$nextTick(() => this.getAndSetHeight())
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.getAndSetHeight)
  },

  methods: {
    calculateHeight() {
      let height = this.getHeightRecursively(this.$refs.collapsibleContent)
      return height + 21
    },
    getHeightRecursively(rootElement) {
      let height = 0
      if (!rootElement?.children) return height

      const children = rootElement.children
      for (let i = 0; i < children.length; i++) {
        const elem = children[i]
        const computedStyle = window.getComputedStyle(elem)
        if (elem.offsetHeight === 0) {
          height += this.getHeightRecursively(elem)
        } else {
          height += elem.offsetHeight
        }
        height += parseInt(computedStyle.getPropertyValue('margin-top'))
        height += parseInt(computedStyle.getPropertyValue('margin-bottom'))
      }
      return height
    },
    toggle() {
      const state = !this.open
      const container = this.$refs.collapsibleContent

      container.classList.add('is-animated')
      if (!this.accordion) {
        container.style.height = (state ? this.calculateHeight() : 0) + 'px'
      }
      this.open = state
      this.$emit('toggle', state)
    },
    setHeight(height) {
      if (this.open) {
        this.$refs.collapsibleContent.style.height = height + 'px'
      }
      return height
    },
    getAndSetHeight() {
      return this.setHeight(this.calculateHeight())
    },
  },
}
</script>

<style lang="postcss" scoped>
.collapsible__content {
  height: 0;
  line-height: normal;
  overflow: hidden;

  &.is-animated {
    transition: height 0.5s ease-in-out;
  }

  .san-youtube__video-container {
    box-shadow: none;
  }
}

.collapsible__action {
  display: flex;
  align-items: center;
  color: var(--c-secondary-neutral-1);
  user-select: none;
  cursor: pointer;
}

.collapsible__title {
  font-style: normal;
  font-size: 16px;
  line-height: 1.25;
  letter-spacing: 0.25px;

  color: var(--c-primary-neutral-1);

  font-weight: 700;
  transition: color 0.5s ease-in-out;
  margin: 0;
  padding-right: var(--spacing-2);

  display: inline-flex;
  align-items: center;
  min-height: 55px;

  &.has-border {
    border-bottom: 1px solid var(--c-secondary-neutral-1);
  }
}

.collapsible__filler {
  flex: 1;
}

.collapsible__indicator {
  transition: all 0.5s;
  width: 20px;
  height: 20px;
}

.collapsible__indicator--reduced {
  width: 12px;
  height: 12px;
}

.accordion-item {
  position: relative;
  padding: 0;
  margin: 0;
  border-bottom: 1px solid var(--c-secondary-neutral-3);

  .collapsible__action {
    color: var(--c-secondary-neutral-1);
    padding: var(--spacing-1) var(--spacing-2) var(--spacing-1) 0;
    margin: 0;
    cursor: pointer;

    display: flex;
    justify-content: space-between;
    align-items: center;

    .collapsible__title {
      font-weight: 700;
      font-style: normal;
      font-size: 18px;
      line-height: 1.25;
      letter-spacing: normal;
      color: var(--c-primary-neutral-1);
      min-width: 1px;
      max-width: calc(100% - 55px);
      overflow: hidden;
    }
  }

  .collapsible__indicator {
    margin-right: calc(var(--spacing-2) * -1);
  }

  .collapsible__content {
    padding: 0 var(--spacing-6) 0 0;
    opacity: 0;
    max-height: 0;
    visibility: hidden;
    overflow: hidden;
    transform: translate3d(0, 0, 1);
    transition: all 0.7s ease-in-out;
  }

  &.is-open > .collapsible__content {
    visibility: visible;
    opacity: 1;
    max-height: 10000px;
    padding-bottom: var(--spacing-6);
    height: auto;
  }

  &.is-reduced {
    border-bottom: 1px solid var(--c-primary-neutral-1);

    .collapsible__action {
      padding: var(--spacing-2);

      .collapsible__title {
        font-size: 15px;
        min-height: 0;
        max-width: 100%;
      }
    }

    &.is-open > .collapsible__content {
      padding-bottom: var(--spacing-8);
    }

    .collapsible__content {
      padding-left: var(--spacing-2);
    }
  }

  @media (--xs) {
    &.is-open > .collapsible__action {
      padding-bottom: var(--spacing-4);
    }

    .collapsible__content {
      padding: 0 var(--spacing-8) 0 0;
    }
  }
}
</style>
